export const DISPLAY_TIME_ZONE = 'Europe/Bratislava';

const MINUTE_MS = 60 * 1000;

const isKnown = value => value !== null && value !== undefined;

export const formatDuration = ms => {
  if (!isKnown(ms)) return 'bez údajov';
  if (ms > 0 && ms < MINUTE_MS) return 'menej ako 1 min';
  const minutes = Math.round(ms / MINUTE_MS);
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) return `${rest} min`;
  if (rest === 0) return `${hours} h`;
  return `${hours} h ${rest} min`;
};

export const RESOLUTIONS = ['daily', 'weekly', 'monthly', 'yearly'];

const UNITS = {
  daily: ['deň', 'dni', 'dní'],
  weekly: ['týždeň', 'týždne', 'týždňov'],
  monthly: ['mesiac', 'mesiace', 'mesiacov'],
  yearly: ['rok', 'roky', 'rokov'],
};

export const countLabel = (count, resolution) => {
  const [one, few, many] = UNITS[resolution];
  if (count === 1) return `${count} ${one}`;
  if (count >= 2 && count <= 4) return `${count} ${few}`;
  return `${count} ${many}`;
};

const zo = count => (/^(4|6|7|1[467]|[467]\d)$/.test(String(count)) ? 'zo' : 'z');

export const ofLabel = (known, count, resolution) => `${known} ${zo(count)} ${count} ${UNITS[resolution][2]}`;

export const appLabel = app => ({
  primary: app.name || app.identifier,
  identifier: app.identifier,
  hasName: !!app.name,
});

const sumKnown = values => {
  const known = values.filter(isKnown);
  return known.length ? known.reduce((sum, value) => sum + value, 0) : null;
};

export const appTotal = (app, buckets) => {
  const known = buckets.filter(bucket => isKnown(app.durationByBucket[bucket.key]));
  return {
    durationMs: sumKnown(buckets.map(bucket => app.durationByBucket[bucket.key])),
    knownBuckets: known.length,
    missingBuckets: buckets.length - known.length,
    isPartial: known.length < buckets.length,
  };
};

const byKnownDuration = (a, b) => {
  if (isKnown(a) !== isKnown(b)) return isKnown(a) ? -1 : 1;
  return isKnown(a) ? b - a : 0;
};

const coverageOf = buckets =>
  buckets.reduce(
    (coverage, bucket) => {
      if (coverage.reach !== null && bucket.startAt > coverage.reach) {
        coverage.gaps.push({ from: coverage.reach, to: bucket.startAt });
      }
      if (coverage.reach !== null && bucket.startAt < coverage.reach) coverage.selfOverlap = true;
      coverage.reach = coverage.reach === null || bucket.endAt > coverage.reach ? bucket.endAt : coverage.reach;
      return coverage;
    },
    { reach: null, gaps: [], selfOverlap: false }
  );

export const summarizeView = view => {
  const { gaps, selfOverlap } = coverageOf(view.buckets);
  const apps = view.apps
    .map(app => ({ ...app, total: appTotal(app, view.buckets) }))
    .sort(
      (a, b) =>
        byKnownDuration(a.total.durationMs, b.total.durationMs) ||
        appLabel(a).primary.localeCompare(appLabel(b).primary)
    );
  return {
    apps,
    bucketCount: view.buckets.length,
    gaps,
    edgeBuckets: view.buckets.filter(bucket => bucket.partial),
    unalignedCount: view.buckets.filter(bucket => !bucket.aligned).length,
    isPartial: gaps.length > 0,
    additive: !selfOverlap,
    totalMs: sumKnown(apps.map(app => app.total.durationMs)),
  };
};

const pad = value => String(value).padStart(2, '0');

const partsIn = (iso, timeZone) => {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(iso));
  return parts.reduce((all, part) => ({ ...all, [part.type]: part.value }), {});
};

export const formatDateTimeIn = (iso, timeZone = DISPLAY_TIME_ZONE) => {
  const p = partsIn(iso, timeZone);
  return `${p.day}.${p.month}.${p.year} ${p.hour}:${p.minute}`;
};

export const formatDay = day => {
  const [year, month, date] = day.split('-');
  return `${date}.${month}.${year}`;
};

const WEEKDAYS = ['ne', 'po', 'ut', 'st', 'št', 'pi', 'so'];

const toUtc = day => {
  const [year, month, date] = day.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, date));
};

const fromUtc = d => `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;

export const weekday = day => WEEKDAYS[toUtc(day).getUTCDay()];

export const addDays = (day, count) => {
  const d = toUtc(day);
  d.setUTCDate(d.getUTCDate() + count);
  return fromUtc(d);
};

const localDay = iso => {
  const p = partsIn(iso, DISPLAY_TIME_ZONE);
  return `${p.year}-${p.month}-${p.day}`;
};

const isLocalMidnight = iso => {
  const p = partsIn(iso, DISPLAY_TIME_ZONE);
  return p.hour === '00' && p.minute === '00' && new Date(iso).getTime() % MINUTE_MS === 0;
};

const PERIODS = {
  daily: { letter: 'd', starts: () => true, next: day => addDays(day, 1) },
  weekly: { letter: 'w', starts: day => weekday(day) === 'po', next: day => addDays(day, 7) },
  monthly: {
    letter: 'm',
    starts: day => day.slice(8) === '01',
    next: day => {
      const [year, month] = day.split('-').map(Number);
      return fromUtc(new Date(Date.UTC(year, month, 1)));
    },
  },
  yearly: {
    letter: 'y',
    starts: day => day.slice(5) === '01-01',
    next: day => `${Number(day.slice(0, 4)) + 1}-01-01`,
  },
};

// A native period is labelled as a calendar day/week/month/year only when it spans exactly that period in the
// display zone; anything else keeps its exact boundaries.
export const describeBucket = (resolution, { startAt, endAt, partial }) => {
  const period = PERIODS[resolution];
  const firstDay = localDay(startAt);
  const aligned =
    isLocalMidnight(startAt) &&
    isLocalMidnight(endAt) &&
    period.starts(firstDay) &&
    localDay(endAt) === period.next(firstDay);
  const niceKey = {
    daily: firstDay,
    weekly: `w-${firstDay}`,
    monthly: `m-${firstDay.slice(0, 7)}`,
    yearly: `y-${firstDay.slice(0, 4)}`,
  }[resolution];
  return {
    key: aligned ? niceKey : `${period.letter}-${startAt}~${endAt}`,
    startAt,
    endAt,
    partial,
    aligned,
    firstDay,
    lastDay: aligned ? addDays(localDay(endAt), -1) : null,
  };
};

export const viewRange = view => {
  if (!view.buckets.length) return null;
  return {
    startAt: view.buckets[0].startAt,
    endAt: view.buckets.reduce((end, bucket) => (bucket.endAt > end ? bucket.endAt : end), view.buckets[0].endAt),
  };
};

export const formatRange = range => `${formatDateTimeIn(range.startAt)} – ${formatDateTimeIn(range.endAt)}`;

export const bucketLabel = (resolution, bucket) => {
  if (!bucket.aligned) return formatRange(bucket);
  const [year, month] = bucket.firstDay.split('-');
  if (resolution === 'daily') return `${weekday(bucket.firstDay)} ${formatDay(bucket.firstDay).slice(0, 6)}`;
  if (resolution === 'weekly') return `${formatDay(bucket.firstDay).slice(0, 6)}–${formatDay(bucket.lastDay)}`;
  if (resolution === 'monthly') return `${month}/${year}`;
  return year;
};

export const bucketEdgeLabel = bucket => (bucket.partial ? 'čiastočné obdobie podľa telefónu' : null);

export const overlaps = (bucket, range) => !!range && bucket.startAt < range.endAt && bucket.endAt > range.startAt;

export const overlappingBuckets = (view, otherView) => {
  const range = viewRange(otherView);
  return view.buckets.filter(bucket => overlaps(bucket, range));
};



// WEBPACK FOOTER //
// ./src/features/loan-management/components/phone-usage/usageModel.js